/**
 * `Modal` — finestra di dialogo modale riusabile del design system.
 *
 * Caratteristiche di accessibilità:
 *  - `role="dialog"` + `aria-modal="true"` collegato al titolo via
 *    `aria-labelledby`;
 *  - focus spostato sul dialog all'apertura e ripristinato all'elemento
 *    precedente alla chiusura;
 *  - chiusura con `Escape` e con click sul backdrop;
 *  - il body della pagina viene bloccato (`overflow: hidden`) mentre è aperto.
 *
 * È presentazionale/controllato: la visibilità è gestita dal chiamante via
 * `open`; `onClose` viene invocato per ogni richiesta di chiusura (Escape,
 * backdrop, pulsante di chiusura).
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Slot per azioni in footer (es. pulsanti Annulla/Conferma). */
  footer?: ReactNode;
  /**
   * Larghezza del dialog. `'default'` (560px) per i form semplici; `'wide'`
   * per contenuti più ricchi come tabelle (es. editor delle skill collegate).
   */
  size?: 'default' | 'wide';
}

export function Modal({ open, onClose, title, children, footer, size = 'default' }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // All'APERTURA (solo quando `open` cambia): sposta il focus sul dialog una
  // volta e blocca lo scroll del body; alla chiusura ripristina il focus.
  //
  // IMPORTANTE: questo effetto dipende esclusivamente da `open`. Non deve
  // dipendere da `onClose` (spesso una funzione inline ricreata a ogni render
  // del chiamante): altrimenti a ogni digitazione in un input interno l'effetto
  // si ri‑eseguirebbe e `dialogRef.focus()` ruberebbe il focus al campo,
  // facendolo "saltare via" a ogni carattere.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open]);

  // Chiusura con Escape: effetto separato, così aggiornare `onClose` non
  // ri‑esegue la logica di focus sopra.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="ui-modal__backdrop"
      onMouseDown={(e) => {
        // Chiude solo se il click parte dal backdrop, non dal contenuto.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={size === 'wide' ? 'ui-modal ui-modal--wide' : 'ui-modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="ui-modal__header">
          <h2 id={titleId} className="ui-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="ui-modal__close"
            aria-label="Chiudi"
            onClick={onClose}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className="ui-modal__body">{children}</div>
        {footer && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
